import * as tslib_1 from "tslib";
import { ClientErrorCodes, ErrorTranslationKeys, } from '@models';
import { Logger } from '@services/logger/abstract.logger';
import { TranslatableError } from '@utils/translatable-error';
import { SettingsService } from 'main/app-state/current-user/settings/settings.service';
import { NullableStateManager } from 'main/app-state/generics/state-manager';
import { BehaviorSubject, combineLatest, iif, of, throwError, } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap, } from 'rxjs/operators';
import { CurrentUserService } from './current-user.service';
import { FolderManager, } from './folder';
import { LicenseManager } from './license/license.manager';
import { FolderPermissionsManager, PermissionsManager, } from './permissions';
import { SettingsManager, } from './settings';
import { ThemeManager, } from './theme';
var CurrentUserManager = /** @class */ (function (_super) {
    tslib_1.__extends(CurrentUserManager, _super);
    function CurrentUserManager(injector) {
        var _this = _super.call(this) || this;
        _this._state = new BehaviorSubject(null);
        _this.ensureValidFolders = function () {
            // the list of classic folders is stored in userManager
            return _this.fetch()
                .pipe(switchMap(function () { return _this._folder.fetch(); }), map(function (_) { return true; }));
        };
        _this._currentUserService = injector.get(CurrentUserService);
        _this._settingsService = injector.get(SettingsService);
        // The Injector doesn't work directly with abstract classes, so manual casting to Type<Logger> has to be done.
        // More details: https://github.com/angular/angular/issues/23611
        _this._logStateChanges('User', injector.get(Logger));
        _this._themeManager = new ThemeManager(injector, _this);
        _this._folder = new FolderManager(injector, _this);
        _this._settings = new SettingsManager(injector, _this);
        _this._permissions = new PermissionsManager(injector);
        _this._license = new LicenseManager(injector, _this);
        _this._folderPermissions = new FolderPermissionsManager(injector, _this._permissions);
        return _this;
    }
    Object.defineProperty(CurrentUserManager.prototype, "theme", {
        get: function () {
            return this._themeManager;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CurrentUserManager.prototype, "folder", {
        get: function () {
            return this._folder;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CurrentUserManager.prototype, "settings", {
        get: function () {
            return this._settings;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CurrentUserManager.prototype, "permissions", {
        get: function () {
            return this._permissions;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CurrentUserManager.prototype, "folderPermissions", {
        get: function () {
            return this._folderPermissions;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CurrentUserManager.prototype, "license", {
        get: function () {
            return this._license;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CurrentUserManager.prototype, "authState", {
        get: function () {
            if (!this.value) {
                return 0 /* NotLoggedIn */;
            }
            if (!this.value.tenancyName) {
                return 2 /* LoggedInAsHost */;
            }
            return 1 /* LoggedInAsTenant */;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CurrentUserManager.prototype, "loggedInAsHost", {
        get: function () {
            return this.authState === 2 /* LoggedInAsHost */;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Refreshes the current user's data and emits after all child managers have a non-null state.
     */
    CurrentUserManager.prototype.fetch = function () {
        var _this = this;
        return this._currentUserService.get()
            .pipe(switchMap(function (user) {
            if (user) {
                return of(user);
            }
            if (_this.value) {
                // Stale state.
                return throwError("UserShouldBeLoggedOut" /* UserShouldBeLoggedOut */);
            }
            // Normal behaviour. Should skip the initialization of child managers.
            return throwError("UserNotLoggedIn" /* UserNotLoggedIn */);
        }), tap(function (user) { return _this._state.next(user); }), switchMap(function () { return _this._folder.fetch(); }), switchMap(function () { return _this._permissions.fetch(); }), switchMap(function () {
            return combineLatest([
                _this._folder.value$,
                _this._settings.value$,
                _this._permissions.value$,
                _this._license.value$,
            ]);
        }), filter(function (allStates) { return allStates.every(function (state) { return !!state; }); }), take(1), map(function () { return _this.value; }), catchError(function (error) {
            switch (error) {
                case "UserNotLoggedIn" /* UserNotLoggedIn */:
                    return of(null);
                case "UserShouldBeLoggedOut" /* UserShouldBeLoggedOut */:
                    return _this.logout().pipe(map(function (_) { return null; }));
                default:
                    return throwError(error);
            }
        }));
    };
    CurrentUserManager.prototype.ensureCorrectIdentity = function () {
        return this._currentUserService.ensureCorrectIdentity();
    };
    CurrentUserManager.prototype.login = function (payload) {
        var _this = this;
        payload.tenancyName = (payload.tenancyName || '').trim();
        payload.usernameOrEmailAddress = (payload.usernameOrEmailAddress || '').trim();
        return this.ensureCorrectIdentity()
            .pipe(switchMap(function () { return _this._currentUserService.login(payload); }), switchMap(function () { return _this.ensureCorrectIdentity(); }), switchMap(function () { return _this.fetch(); }));
    };
    CurrentUserManager.prototype.logout = function () {
        var _this = this;
        return this._currentUserService.logout()
            .pipe(switchMap(function () { return _this._permissions.clean(); }), switchMap(function () { return _this._folder.clean(); }), tap(function () { return _this._state.next(null); }), switchMap(function () {
            return combineLatest([
                _this._folder.value$,
                _this._settings.value$,
                _this._permissions.value$,
                _this._license.value$,
            ]);
        }), filter(function (allStates) { return allStates.every(function (state) { return !state; }); }), take(1), map(function () { return true; }));
    };
    CurrentUserManager.prototype.update = function (data) {
        var _this = this;
        return iif(function () { return !!_this.value; }, this._currentUserService
            .update(this.value.id, data)
            .pipe(switchMap(function () { return _this.fetch(); })), throwError(new TranslatableError(ErrorTranslationKeys.SessionExpired, ClientErrorCodes.SessionExpired)));
    };
    CurrentUserManager.prototype.patch = function (data) {
        var _this = this;
        return iif(function () { return !!_this.value; }, this._currentUserService
            .patch(this.value.id, data)
            .pipe(switchMap(function () { return _this.fetch(); })), throwError(new TranslatableError(ErrorTranslationKeys.SessionExpired, ClientErrorCodes.SessionExpired)));
    };
    CurrentUserManager.prototype.saveLanguage = function (languageCode) {
        var _this = this;
        return this._settingsService.setLanguage(languageCode)
            .pipe(switchMap(function () { return _this.settings.fetch(); }), map(function (_) { return _this.value; }));
    };
    return CurrentUserManager;
}(NullableStateManager));
export { CurrentUserManager };
